import PropTypes from 'prop-types';
import React, {useEffect, useState} from 'react';
import { List } from 'immutable';
import classnames from 'classnames';
import _ from 'lodash';

import { translate as t } from '../../../lib/I18n';
import Station from '../../../records/Station';
import Search from '../../../records/Search';
import SeasonSearch from '../../../records/SeasonSearch';
import StationFinderStore from '../../../stores/StationFinderStore';
import StationFinderActions from '../../../actions/StationFinderActions';
import NotificationActions from '../../../actions/NotificationActions';
import connectToStores from '../../../helpers/connectToStores';

import StationSearchOverlay from './SearchOverlay';
import StationFinderModal from './../StationFinderModal';
import OpenMapComponent from './../../openMap';
import LabelPopup from './../../openMap/LabelPopup';
import OpenMapLinePath from './../../openMap/LinePath';
import StationGroup from './StationGroup';

const StationFinder = ({
    className,
    origin,
    destination,
    stations,
    error,
    isExpanded,
    onChange,
    isMobile,
    closeContainerModal,
    currentSearch,
}) => {
    const [selectedStation, setSelectedStation] = useState(null);
    const [showStationFinderModal, setShowStationFinderModal] = useState(false);
    const [isSearchingStations, setIsSearchingStations] = useState(false);
    const [ticketOffice, setTicketOffice] = useState(null);
    const [loadingTicketOfficeDetails, setLoadingTicketOfficeDetails] = useState(false);
    const [positionMarkerCoords, setPositionMarkerCoords] = useState(null);

    useEffect(()=> {
        if (stations.size) {
            _.defer(StationFinderActions.clearStations);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(()=> {
        _.defer(StationFinderActions.clearStations);
        setPositionMarkerCoords(null);
        if (isExpanded) {
            StationFinderActions.setExpanded(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [origin, destination]);

    useEffect(()=> {
        if (stations.size > 0 || error) {
            setIsSearchingStations(false);
        }
    }, [stations, error]);

    const onSelectPosition = (coords) => {
        setPositionMarkerCoords(coords);
    };

    const handleSearchClear = () => {
        setPositionMarkerCoords(null);
    };

    const handleSearch = ({ lat: latitude, lng: longitude }, searchInteraction) => {
        setIsSearchingStations(true);
        StationFinderActions.findNearest({ latitude, longitude, searchInteraction });
    };

    const handleStationSelected = async (station) => {
        setShowStationFinderModal(true);
        setSelectedStation(station);
        setTicketOffice(null);
        setLoadingTicketOfficeDetails(true);

        try {
            const fetchedTicketOffice = await StationFinderActions.getTicketOfficeDetails(station);
            setTicketOffice(fetchedTicketOffice);
        } catch (e) {
            console.warn(`error loading opening hours`, e);
        }
        setLoadingTicketOfficeDetails(false);
    };

    const handleStationFinderModalClose = () => {
        setShowStationFinderModal(false);
    };

    const handleStationChange = (type, value) => {
        StationFinderActions.setStationSelected(type, value);

        if (isMobile) {
            _.defer(StationFinderActions.clearStations);
        }

        onChange(type, value);

        if (isMobile) {
            const updatedText = `${_.capitalize(type)} ${t('stationFinder.modal.update')} ${value.name}`;
            NotificationActions.showNotification(updatedText);
            closeContainerModal();
        }
    };

    return (
        <div className={ classnames('stationFinder__container', {
            'mobile': isMobile
        }) }>
            <OpenMapComponent className={'map ' + className}>
                { origin.latitude && (
                    <LabelPopup 
                        coords={origin.getLatLng()} 
                        className="search-station-popup" 
                        label={`${ t('global.start') }: <strong>${ origin.name }</strong>`}
                        isAutoPan={origin.latitude && destination.latitude}
                    />)
                }
                { destination.latitude && (
                    <LabelPopup 
                        coords={destination.getLatLng()} 
                        className="search-station-popup" 
                        label={`${ t('global.end') }: <strong>${ destination.name }</strong>`}
                        isAutoPan={origin.latitude && destination.latitude}
                    />)
                }
                {(origin.latitude && destination.latitude) && <OpenMapLinePath styles={{dashArray: '5, 10'}} pathArray={[origin.getLatLng(), destination.getLatLng()]}/> }
                <StationSearchOverlay
                    error={ error }
                    isExpanded={ isExpanded }
                    onClear={ handleSearchClear }
                    isMobile={ isMobile }
                    onSelectPosition={ onSelectPosition }
                    onSearch={ handleSearch }
                    positionMarkerCoords={ positionMarkerCoords }
                    setPositionMarkerCoords={ onSelectPosition }
                    setShowStationFinderModal={ setShowStationFinderModal }
                    isSearchingStations={ isSearchingStations }/>
                <StationFinderModal
                    selectedStation={ selectedStation }
                    ticketOffice={ ticketOffice }
                    show={ showStationFinderModal }
                    onClose={ handleStationFinderModalClose }
                    onContinue={ handleStationChange }
                    loadingTicketOfficeDetails={ loadingTicketOfficeDetails }
                    currentSearch={ currentSearch }/>
                <StationGroup stations={stations} onSelect={handleStationSelected}/>
            </OpenMapComponent>
        </div>
    );
};

StationFinder.propTypes = {
    className: PropTypes.string,
    origin: PropTypes.instanceOf(Station),
    destination: PropTypes.instanceOf(Station),
    stations: PropTypes.instanceOf(List),
    error: PropTypes.instanceOf(Error),
    isExpanded: PropTypes.bool,
    onChange: PropTypes.func.isRequired,
    isMobile: PropTypes.bool.isRequired,
    closeContainerModal: PropTypes.func,
    currentSearch: PropTypes.oneOfType([
        PropTypes.instanceOf(Search),
        PropTypes.instanceOf(SeasonSearch)
    ]),
};

function getStateFromStores () {
    return {
        stations: StationFinderStore.get('stations'),
        error: StationFinderStore.get('error'),
        isExpanded: StationFinderStore.get('isExpanded'),
    };
}

export default connectToStores([ StationFinderStore ], getStateFromStores)(StationFinder);



// WEBPACK FOOTER //
// ./src/components/stationFinder/OpenStreetMaps/index.js